import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from "@mui/material";
import RestAPIService from "../../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../../Utility/API/Serviceurls";
import ShowComponents from "../Common/ShowComponent";
import { Colors } from "../../../../Styles/Colors";
import { renderTitle } from "../Common/DocCommonComponent";

const DietPlan = ({ Title, appointmentId, ipAdmissionId, clinicId, doctorId, isFromIp, patientId, isSelected, successMessage, errorMessage }) => {

  const [state, setState] = useState({
    id: null,
    dietType: "REGULAR",
    instructions: "",
    isLoading: false,
    isTransmitted: false
  });

  const updateState = (obj) =>
    setState((prev) => ({ ...prev, ...obj }));

  /* ================= FETCH DIET PLAN ================= */
  const getDietPlan = useCallback(() => {
    const params = isFromIp ? `?ip_admission_id=${ipAdmissionId}&clinic_id=${clinicId}&doctor_id=${doctorId}` : `?appointment_id=${appointmentId}`;
    RestAPIService.getAll(
      Serviceurls.AYURVEDA_DIET_PLAN + params
    )
      .then((res) => {
        if (res.data.status === "success" && res.data.data) {
          const data = res.data.data;
          updateState({
            id: data.id,
            dietType: data.diet_type,
            instructions: data.instructions || "",
            isTransmitted: data.is_transmit || false
          });
        }
      })
      .catch((err) => errorMessage(err?.message));
  }, [appointmentId, isFromIp, ipAdmissionId]);

  /* ================= SAVE ================= */
  const saveDietPlan = () => {
    if (!state.instructions)
      return errorMessage("Instructions are required");

    const payload = {
      patient_id: patientId,
      diet_type: state.dietType,
      instructions: state.instructions
    };
    if (isFromIp) {
      payload.ip_admission_id = ipAdmissionId;
      payload.clinic_id = clinicId;
      payload.doctor_id = doctorId;
    } else {
      payload.appointment_id = appointmentId;
    }

    updateState({ isLoading: true });

    const method = state.id ? "NewupdateWithOutId" : "create";

    RestAPIService.create(payload, Serviceurls.AYURVEDA_DIET_PLAN)
      .then((res) => {
        if (res.data.status === "success") {
          successMessage(res.data.message);
          getDietPlan();
        } else {
          errorMessage(res?.data?.message);
        }
      })
      .catch((err) =>
        errorMessage(err?.response?.data?.message || err?.message)
      )
      .finally(() => updateState({ isLoading: false }));
  };

  /* ================= CLEAR ================= */
  const clearForm = () => {
    updateState({
      id: null,
      dietType: "REGULAR",
      instructions: ""
    });
  };

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    getDietPlan();
  }, [getDietPlan]);

  return (
    <ShowComponents onIntersection={getDietPlan}>
      <Card
        sx={{
          width: "100%",
          padding: "0.5vw",
          border: `2px solid ${isSelected ? Colors.themeDark : Colors.white}`
        }}
      >
        {/* ===== HEADER ===== */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          borderBottom="1px solid #e0e0e0"
          padding="0.5vw"
        >
          {renderTitle(Title || "Diet Plan", isSelected)}

          <Box display="flex" gap="0.8vw">
            <Button
              variant="outlined"
              color="inherit"
              onClick={clearForm}
              disabled={state.isTransmitted}
            >
              CLEAR
            </Button>

            <Button
              variant="contained"
              onClick={saveDietPlan}
              disabled={state.isLoading || state.isTransmitted}
            >
              SAVE
            </Button>
          </Box>
        </Box>

        {/* ===== DIET TYPE ===== */}
        <Box mt={1}>
          <FormControl>
            <RadioGroup
              row
              value={state.dietType}
              onChange={(e) =>
                updateState({ dietType: e.target.value })
              }
            >
              <FormControlLabel
                value="REGULAR"
                control={<Radio />}
                label="Regular Diet"
              />
              <FormControlLabel
                value="RENAL"
                control={<Radio />}
                label="Renal Diet"
              />
              <FormControlLabel
                value="DIABETIC"
                control={<Radio />}
                label="Diabetics"
              />
              <FormControlLabel
                value="WEIGHTLOSS"
                control={<Radio />}
                label="Weight Loss"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {/* ===== INSTRUCTIONS ===== */}
        <Box mt={1}>
          <Typography fontWeight={600} mb={0.5}>
            Instructions *
          </Typography>

          <TextField
            multiline
            rows={6}
            fullWidth
            value={state.instructions}
            inputProps={{
              maxLength: 500
            }}
            disabled={state.isTransmitted}
            onChange={(e) =>
              updateState({ instructions: e.target.value })
            }
            placeholder="Enter diet instructions here..."
          />
        </Box>
      </Card>
    </ShowComponents>
  );
};

export default DietPlan;
